import React, { useState } from 'react'
import { useModalBehavior } from 'hooks'
import { formatCurrency } from 'utils/currencyFormatter'

const INVOICE_TYPES = [
  { value: 'personal', labelKey: 'personal', defaultLabel: '個人電子發票' },
  { value: 'company', labelKey: 'company', defaultLabel: '公司用（統編）' },
  { value: 'mobile_carrier', labelKey: 'mobileCarrier', defaultLabel: '手機載具' },
  // TODO: 捐贈發票需要加愛心碼選單 UX，暫時隱藏
  // { value: 'donation', labelKey: 'donation', defaultLabel: '捐贈發票' },
]

export default function InvoiceInfoModal({ isOpen, onClose, onConfirm, translations, userEmail, purchaseInfo, discount, installmentConfig, amount, currency }) {
  const [invoiceType, setInvoiceType] = useState('personal')
  const [identifier, setIdentifier] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [email, setEmail] = useState(userEmail || '')
  const [carrierNumber, setCarrierNumber] = useState('')
  const [loveCode, setLoveCode] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('full')
  const [error, setError] = useState('')

  const { handleOverlayClick } = useModalBehavior(isOpen, onClose)

  if (!isOpen) return null

  const t = translations?.invoice || {}
  const title = t.title || '電子發票'
  const hasDiscount = discount && discount.applied > 0
  const cur = purchaseInfo?.pkg?.currency || currency || 'TWD'

  // Installment guards: only offer it for TWD orders at/above the configured
  // minimum (banks reject installment credit below their threshold). The period
  // list and threshold come from the server (TeachingTeamConstants) — no literals.
  // We render one option per allowed period so expanding the server whitelist
  // never leaves the UI silently showing a subset.
  const effectiveAmount = hasDiscount ? discount.final_amount : (purchaseInfo?.pkg?.totalPrice ?? amount)
  const installmentPeriods = installmentConfig?.installmentPeriods || []
  const showInstallment = Boolean(
    installmentPeriods.length > 0 &&
    cur === 'TWD' &&
    typeof effectiveAmount === 'number' &&
    effectiveAmount >= (installmentConfig?.installmentMinAmount ?? Infinity)
  )

  const validate = () => {
    switch (invoiceType) {
      case 'company':
        if (!/^\d{8}$/.test(identifier.trim())) {
          setError(t.taxIdInvalid || '統一編號須為 8 碼數字')
          return false
        }
        break
      case 'mobile_carrier':
        if (!/^\/[0-9A-Z+\-.]{7}$/.test(carrierNumber.trim())) {
          setError(t.carrierInvalid || '手機載具碼格式錯誤（/ 加 7 碼）')
          return false
        }
        break
      case 'donation':
        if (!/^\d{3,7}$/.test(loveCode.trim())) {
          setError(t.loveCodeInvalid || '愛心碼須為 3-7 碼數字')
          return false
        }
        break
    }
    setError('')
    return true
  }

  const handleConfirm = () => {
    if (!validate()) return

    const data = {
      invoice_type: invoiceType,
      customer_identifier: '',
      customer_name: '',
      customer_email: email || userEmail || '',
      carrier_type: '',
      carrier_number: '',
      love_code: ''
    }

    // Installment period for the ECPay checkout redirect, only when the option was
    // offered and a real period selected. Omitted entirely otherwise (pay-in-full,
    // below threshold, or non-TWD) so it never leaks into a pay-in-full checkout.
    if (showInstallment && installmentPeriods.includes(paymentMethod)) {
      data.installment = paymentMethod
    }

    switch (invoiceType) {
      case 'company':
        data.customer_identifier = identifier.trim()
        data.customer_name = companyName.trim()
        break
      case 'mobile_carrier':
        data.carrier_type = '3'
        data.carrier_number = carrierNumber.trim()
        break
      case 'donation':
        data.love_code = loveCode.trim()
        break
    }

    onConfirm(data)
  }

  const handleTypeChange = (type) => {
    setInvoiceType(type)
    setError('')
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 lg:p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white w-full h-full lg:h-auto lg:max-h-[90vh] lg:max-w-lg lg:mx-4 lg:rounded-xl lg:shadow-xl overflow-hidden flex flex-col"
      >
        {/* Mobile header — back arrow */}
        <div className="lg:hidden px-4 py-3 bg-white border-b border-gray-100 flex items-center gap-3 shrink-0">
          <button onClick={onClose} className="flex items-center text-gray-600 cursor-pointer" aria-label="Back">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-gray-900 truncate">{title}</span>
        </div>
        {/* Desktop header — blue bar */}
        <div
          className="hidden lg:flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ backgroundColor: '#E6F0FF' }}
        >
          <span className="font-semibold" style={{ color: '#3366FF' }}>{title}</span>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1">
          {/* Purchase summary */}
          {purchaseInfo?.course && purchaseInfo?.pkg && (
            <div className="mb-5 p-4 rounded-xl" style={{ backgroundColor: '#F8FAFC' }}>
              <p className="text-sm font-medium text-gray-900">{purchaseInfo.course.name}</p>
              <p className="text-sm text-gray-500 mt-0.5">
                {purchaseInfo.pkg.lessonCount} {t.lessons || '堂'} × {purchaseInfo.pkg.durationMinutes || 60} {t.minutes || '分鐘'}
              </p>
              {hasDiscount ? (
                <div className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between text-gray-500">
                    <span>{t.subtotal || '原價'}</span>
                    <span>{formatCurrency(discount.original_amount, cur)}</span>
                  </div>
                  {discount.welcome_applied > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t.welcomeDiscount || '新生折價券'}</span>
                      <span>-{formatCurrency(discount.welcome_applied, cur)}</span>
                    </div>
                  )}
                  {discount.referral_applied > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>{t.referralDiscount || '推薦折扣'}</span>
                      <span>-{formatCurrency(discount.referral_applied, cur)}</span>
                    </div>
                  )}
                  <div className="border-t border-gray-200 my-1.5"></div>
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium text-gray-900">{t.total || '實付'}</span>
                    <span className="text-lg font-bold" style={{ color: '#3366FF' }}>
                      {formatCurrency(discount.final_amount, cur)}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex items-baseline justify-end mt-1">
                  <span className="text-lg font-bold" style={{ color: '#3366FF' }}>
                    {formatCurrency(purchaseInfo.pkg.totalPrice, cur)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Payment method: pay in full vs installment (TWD + above threshold only) */}
          {showInstallment && (
            <div className="mb-5">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {t.paymentMethod || 'Payment method'}
              </p>
              <div className="space-y-2">
                {[
                  { value: 'full', label: t.payInFull || 'Pay in full' },
                  ...installmentPeriods.map((period) => ({
                    value: period,
                    label: (t.installmentOption || '{n}-month installment').replace('{n}', period)
                  }))
                ].map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      paymentMethod === value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="paymentMethod"
                      value={value}
                      checked={paymentMethod === value}
                      onChange={() => setPaymentMethod(value)}
                      className="w-4 h-4 text-blue-500 cursor-pointer"
                    />
                    <span className={paymentMethod === value ? 'text-blue-700 font-medium' : 'text-gray-700'}>
                      {label}
                    </span>
                  </label>
                ))}
              </div>
              {installmentPeriods.includes(paymentMethod) && (
                <p className="text-xs text-gray-500 mt-2">
                  {t.installmentNote || 'Installments are handled by your card issuer; terms depend on your bank.'}
                </p>
              )}
            </div>
          )}

          {/* Invoice type radio buttons */}
          <div className="space-y-2 mb-5">
            {INVOICE_TYPES.map(({ value, labelKey, defaultLabel }) => (
              <label
                key={value}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  invoiceType === value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="invoiceType"
                  value={value}
                  checked={invoiceType === value}
                  onChange={() => handleTypeChange(value)}
                  className="w-4 h-4 text-blue-500 cursor-pointer"
                />
                <span className={invoiceType === value ? 'text-blue-700 font-medium' : 'text-gray-700'}>
                  {t[labelKey] || defaultLabel}
                </span>
              </label>
            ))}
          </div>

          {/* Company fields */}
          {invoiceType === 'company' && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.taxIdLabel || '統一編號'} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  value={identifier}
                  onChange={(e) => {
                    setIdentifier(e.target.value.replace(/\D/g, '').slice(0, 8))
                    if (error) setError('')
                  }}
                  placeholder="12345678"
                  maxLength={8}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.companyNameLabel || '發票抬頭'}
                </label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t.companyNamePlaceholder || '公司名稱'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t.emailLabel || '電子郵件'}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>
          )}

          {/* Mobile carrier field */}
          {invoiceType === 'mobile_carrier' && (
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.carrierNumberLabel || '手機載具碼'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={carrierNumber}
                onChange={(e) => {
                  setCarrierNumber(e.target.value.toUpperCase().slice(0, 8))
                  if (error) setError('')
                }}
                placeholder="/ABC+123"
                maxLength={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t.carrierHint || '格式：/ 加 7 碼（例如 /ABC+123）'}
              </p>
            </div>
          )}

          {/* Donation field */}
          {invoiceType === 'donation' && (
            <div className="border-t pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t.loveCodeLabel || '愛心碼'} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={loveCode}
                onChange={(e) => {
                  setLoveCode(e.target.value.replace(/\D/g, '').slice(0, 7))
                  if (error) setError('')
                }}
                placeholder="168001"
                maxLength={7}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t.loveCodeHint || '3-7 碼數字'}
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <p className="text-sm text-red-500 mt-3">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-4 border-t flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer hidden lg:block"
          >
            {t.cancel || '取消'}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="px-6 py-2 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer w-full lg:w-auto"
            style={{ backgroundColor: '#3366FF' }}
          >
            {t.confirm || '前往付款'}
          </button>
        </div>
      </div>
    </div>
  )
}
